import { useEffect, useState } from "react";
import { Check, Target } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { HELP } from "../../lib/bookTabs";
import { CelebrationDialog } from "../celebrate/CelebrationDialog";
import { PublishControls } from "../celebrate/PublishControls";

const Bar = ({ pct, tone = "bg-amber-500", testId }) => (
  <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden" data-testid={testId}>
    <div className={`h-full rounded-full ${tone} transition-[width] duration-700`} style={{ width: `${pct}%` }} />
  </div>
);

const sizeText = (s) => `${s.current.toLocaleString()} / ${s.target.toLocaleString()} ${s.unit}`;

export const ProgressSummary = ({ p, bookId, onGo }) => {
  if (!p) return null;
  return (
    <div className="space-y-2.5 mb-4" data-testid={`book-progress-${bookId}`}>
      <div className="flex justify-between text-[11px] text-slate-400"><span>Publishing steps</span><span className="font-mono text-amber-200" data-testid={`book-progress-steps-${bookId}`}>{p.done}/{p.total}</span></div>
      <Bar pct={p.percent} testId={`book-progress-bar-${bookId}`} />
      {p.size.target > 0 && (<>
        <div className="flex justify-between text-[11px] text-slate-400"><span>Target</span><span className="font-mono text-emerald-300" data-testid={`book-progress-size-${bookId}`}>{sizeText(p.size)}</span></div>
        <Bar pct={p.size.percent} tone="bg-emerald-500" testId={`book-progress-size-bar-${bookId}`} />
      </>)}
      <p className="text-xs text-slate-400 truncate" data-testid={`book-progress-next-${bookId}`}>{p.next ? <>Next: <button type="button" onClick={() => onGo(p.next.tab)} className="text-amber-300 hover:underline">{p.next.label}</button></> : <span className="text-emerald-300">All steps done</span>}</p>
    </div>
  );
};

const TargetForm = ({ bookId, size, onSaved }) => {
  const [v, setV] = useState(size.target || "");
  useEffect(() => setV(size.target || ""), [size.target]);
  const save = async () => { try { onSaved((await api.put(`/books/${bookId}/progress-target`, { target: Number(v) || 0 })).data); toast.success("Target saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Target size={14} className="text-emerald-300" />
      <input type="number" min={0} value={v} onChange={(e) => setV(e.target.value)} placeholder="Target" className="w-36 h-8 rounded-md bg-slate-900 border border-slate-700 px-2 text-sm" data-testid="progress-target-input" />
      <span className="text-xs text-slate-500">{size.unit}</span>
      <button type="button" onClick={save} className="h-8 px-3 rounded-md text-xs bg-emerald-700 hover:bg-emerald-600 text-white" data-testid="progress-target-save">Save</button>
    </div>
  );
};

export const ProgressPanel = ({ bookId, onGo, refreshKey }) => {
  const [p, setP] = useState(null);
  const [cel, setCel] = useState(null);
  useEffect(() => { api.get(`/books/${bookId}/progress`).then((r) => setP(r.data)).catch(() => {}); }, [bookId, refreshKey]);
  useEffect(() => {
    if (!p?.celebrate_due) return;
    setP((x) => ({ ...x, celebrate_due: false }));
    api.post(`/books/${bookId}/celebrate-complete`).then((r) => setCel(r.data.celebration)).catch(() => {});
  }, [p?.celebrate_due, bookId]);
  if (!p) return <div className="panel mb-6 h-[200px] animate-pulse" data-testid="book-progress-loading" />;
  return (
    <div className="panel p-5 mb-6 space-y-4" data-testid="book-progress-panel">
      <CelebrationDialog c={cel} onClose={() => setCel(null)} />
      <div className="flex flex-wrap items-center justify-between gap-2"><p className="eyebrow">Progress · {p.percent}%</p>
        <div className="flex flex-wrap items-center gap-3">{p.next && <button type="button" onClick={() => onGo(p.next.tab)} className="text-xs text-amber-300 hover:underline" data-testid="progress-next-link">Next: {p.next.label} →</button>}
          <PublishControls bookId={bookId} published={p.published} onChange={(v) => setP((x) => ({ ...x, published: v }))} onCelebrate={setCel} /></div></div>
      {p.next ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3" data-testid="next-step-card">
          <div className="min-w-0 flex-1"><p className="text-[10px] uppercase tracking-[0.18em] text-amber-300">Do this next</p><p className="text-sm text-slate-100 font-medium" data-testid="next-step-label">{p.next.label}</p>{HELP[p.next.tab] && <p className="text-xs text-slate-400 mt-0.5">{HELP[p.next.tab]}</p>}</div>
          <button type="button" onClick={() => onGo(p.next.tab)} className="h-9 px-4 rounded-md bg-amber-600 hover:bg-amber-500 text-white text-sm transition-colors" data-testid="next-step-go">Go there →</button>
        </div>
      ) : <p className="text-sm text-emerald-300" data-testid="next-step-done">Every step is done — your project is live.</p>}
      <Bar pct={p.percent} testId="progress-steps-bar" />
      <ol className="flex flex-wrap gap-2" data-testid="progress-steps">
        {p.steps.map((s, i) => (
          <li key={s.label}><button type="button" onClick={() => onGo(s.tab)} data-testid={`progress-step-${i}`}
            className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1 border transition-colors ${s.done ? "border-emerald-500/40 text-emerald-200 bg-emerald-900/20" : "border-slate-700 text-slate-400 hover:border-amber-500/50"}`}>
            {s.done ? <Check size={12} /> : <span className="font-mono text-[10px]">{i + 1}</span>}{s.label}</button></li>
        ))}
      </ol>
      <div className="border-t border-slate-800 pt-4 space-y-2">
        {p.size.target > 0 && (<><div className="flex justify-between text-xs text-slate-400"><span>Towards your target</span><span className="font-mono text-emerald-300" data-testid="progress-size-text">{sizeText(p.size)} · {p.size.percent}%</span></div><Bar pct={p.size.percent} tone="bg-emerald-500" testId="progress-size-bar" /></>)}
        {!p.size.target && <p className="text-xs text-slate-500">Set a target to track how far along the {p.size.unit} are (currently {p.size.current.toLocaleString()}).</p>}
        <TargetForm bookId={bookId} size={p.size} onSaved={setP} />
      </div>
    </div>
  );
};
